import { NEW_BLOCK_UNIT_HEIGHT } from '@doss/common-all'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import {
  ChartSubType,
  GetDetailedWorkspaceDocument,
  useCreateViewMutation,
  ViewableObjectType,
} from '../../common/generated/apollo-operations'
import { useTypedTranslation } from '../../common/hooks/useTypedTranslation'
import { Button } from '../ui/atoms/button/Button'
import { BlockType, CreateBlockButtonMenu } from './CreateBlockButtonMenu'
import { TableMenuProvider } from './TableMenuProvider'

type AddNewBlockButtonProps = {
  workspaceId?: string | null
  children?: React.ReactNode
  hideChevron?: boolean
}

export const AddNewBlockButton = ({
  hideChevron,
  workspaceId,
  children,
}: AddNewBlockButtonProps) => {
  const navigate = useNavigate()
  const { t } = useTypedTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [createView] = useCreateViewMutation({
    refetchQueries: [
      {
        query: GetDetailedWorkspaceDocument,
        variables: { workspaceId },
      },
    ],
  })

  const handleCreateNewBlock = ({
    tableId,
    formId,
    selectedBlockType,
  }: {
    tableId: string
    formId?: string
    selectedBlockType: BlockType
  }) => {
    if (selectedBlockType === BlockType.CHART) {
      navigate(`/chart/new?workspaceId=${workspaceId}&tableId=${tableId}`)
    } else if (selectedBlockType === BlockType.METRIC) {
      navigate(
        `/chart/new?workspaceId=${workspaceId}&tableId=${tableId}&chartSubType=${ChartSubType.METRIC}`,
      )
    } else if (selectedBlockType === BlockType.PIVOT_TABLE) {
      navigate(`/pivot/new?workspaceId=${workspaceId}&tableId=${tableId}`)
    } else if (selectedBlockType === BlockType.FORM) {
      if (!formId) {
        return
      }
      createView({
        variables: {
          input: {
            view: {
              parentWorkspaceId: workspaceId || '',
              unitHeight: NEW_BLOCK_UNIT_HEIGHT,
            },
            viewableObjectId: formId,
            viewableObjectType: ViewableObjectType.FORM_V2,
          },
        },
      })
    } else if (selectedBlockType === BlockType.EMBED) {
      createView({
        variables: {
          input: {
            view: {
              parentWorkspaceId: workspaceId || '',
              unitHeight: NEW_BLOCK_UNIT_HEIGHT,
            },
            viewableObjectId: '',
            viewableObjectType: ViewableObjectType.EMBED,
          },
        },
      })
    } else {
      createView({
        variables: {
          input: {
            view: {
              parentWorkspaceId: workspaceId || '',
              unitHeight: NEW_BLOCK_UNIT_HEIGHT,
            },
            viewableObjectId: tableId,
            viewableObjectType: ViewableObjectType.TABLE,
          },
        },
      })
    }
  }
  return (
    <div className="py-3 text-xs">
      <Button
        className="!text-xs"
        endIcon={hideChevron ? undefined : <ChevronDown size={14} />}
        onClick={(e) => {
          setAnchorEl(e.currentTarget)
          setOpen(true)
        }}
        variant="primary"
      >
        {children || t('workspace.add_new')}
      </Button>
      <TableMenuProvider>
        <CreateBlockButtonMenu
          buttonRef={anchorEl}
          onClose={() => {
            setAnchorEl(null)
            setOpen(false)
          }}
          onSelect={handleCreateNewBlock}
          open={open}
        />
      </TableMenuProvider>
    </div>
  )
}
